import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { generateEstimatePdf } from "@/utils/estimates.functions";
import { downloadPdfFromBase64 } from "@/lib/report-export";
import { PdfCanvasPreview } from "@/components/settings/PdfCanvasPreview";
import { ScopePhotosUploader, type ScopePhoto } from "@/components/ScopePhotosUploader";
import { hydrateScopePhotos, serializeScopePhotos } from "@/lib/scope-photos";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Trash2,
  Calendar,
  Building2,
  FileSignature,
  Send,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Pencil,
  Plus,
  Save,
  X,
  FileDown,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import { EmailSendHistoryCard } from "@/components/estimates/EmailSendHistoryCard";
import { ChangeOrdersCard } from "@/components/estimates/ChangeOrdersCard";

export const Route = createFileRoute("/_app/estimates/$estimateId")({
  component: EstimateDetailPage,
});

type Status = "draft" | "sent" | "approved" | "rejected" | "expired";

type Estimate = {
  id: string;
  number: string;
  title: string;
  description: string | null;
  status: Status;
  issue_date: string;
  expiry_date: string | null;
  currency: string;
  tax_percent: number;
  subtotal: number;
  tax_amount: number;
  total: number;
  notes: string | null;
  terms: string | null;
  client_id: string;
  company_id: string;
  scope_photos: unknown;
  clients: { name: string; email: string | null } | null;
};

type Item = {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  sort_order: number;
};

const statusVariant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "outline",
  approved: "default",
  rejected: "destructive",
  expired: "secondary",
};

type ApprovalToken = {
  id: string;
  token: string;
  status: "sent" | "approved" | "rejected" | "revoked";
  client_email: string | null;
  created_at: string;
  responded_at: string | null;
  response_notes: string | null;
  client_signature_data: string | null;
  client_signature_name: string | null;
};

type ScopeBlockView = { title: string; body: string };

type ScopeType = "scope" | "out_of_scope" | "scope_change";
type EditScopeBlock = { id: string; type: ScopeType; content: string };

const SCOPE_OPTIONS: { value: ScopeType; icon: typeof CheckCircle2; color: string }[] = [
  { value: "scope", icon: CheckCircle2, color: "text-emerald-600" },
  { value: "out_of_scope", icon: XCircle, color: "text-destructive" },
  { value: "scope_change", icon: RefreshCw, color: "text-amber-600" },
];

const SCOPE_LABEL_KEYS: Record<ScopeType, string> = {
  scope: "pages.estimates.form.scope",
  out_of_scope: "pages.estimates.form.outOfScope",
  scope_change: "pages.estimates.form.scopeChange",
};

const SCOPE_PLACEHOLDER_KEYS: Record<ScopeType, string> = {
  scope: "pages.estimates.form.scopePlaceholder",
  out_of_scope: "pages.estimates.form.outOfScopePlaceholder",
  scope_change: "pages.estimates.form.scopeChangePlaceholder",
};

/**
 * Maps a heading string (in any of the supported languages) back to a scope
 * type so the editor can pre-select the correct option when the user opens
 * an existing estimate. Falls back to "scope" for unknown headings.
 */
function headingToScopeType(raw: string): ScopeType {
  const k = raw.trim().toLowerCase();
  if (k === "fora de escopo" || k === "out of scope" || k === "fuera de alcance")
    return "out_of_scope";
  if (
    k === "modificação de escopo" ||
    k === "modificacao de escopo" ||
    k === "scope change" ||
    k === "cambio de alcance"
  )
    return "scope_change";
  return "scope";
}

/**
 * Splits the markdown-style description ("## Heading\nbody...") into separate
 * blocks so each scope section gets its own card with the right title.
 * Falls back to a single "Scope" block when no headings are present.
 */
function parseScopeBlocks(description: string): ScopeBlockView[] {
  const text = description.trim();
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const blocks: ScopeBlockView[] = [];
  let current: ScopeBlockView | null = null;
  for (const line of lines) {
    const heading = line.match(/^##\s+(.+?)\s*$/);
    if (heading) {
      if (current) blocks.push({ ...current, body: current.body.trim() });
      current = { title: heading[1], body: "" };
    } else if (current) {
      current.body += (current.body ? "\n" : "") + line;
    } else {
      current = { title: "Scope", body: line };
    }
  }
  if (current) blocks.push({ ...current, body: current.body.trim() });
  return blocks.filter((b) => b.body.length > 0 || b.title.length > 0);
}

/**
 * Same parser as parseScopeBlocks but produces editor-shaped blocks (with a
 * stable id and a typed scope) so the edit form can render Selects + content
 * fields and the user can change the type of a block.
 */
function parseEditScopeBlocks(description: string | null): EditScopeBlock[] {
  if (!description || !description.trim()) {
    return [{ id: crypto.randomUUID(), type: "scope", content: "" }];
  }
  const parsed = parseScopeBlocks(description);
  if (parsed.length === 0) {
    return [{ id: crypto.randomUUID(), type: "scope", content: "" }];
  }
  return parsed.map((b) => ({
    id: crypto.randomUUID(),
    type: headingToScopeType(b.title),
    content: b.body,
  }));
}

function EstimateDetailPage() {
  const { estimateId } = Route.useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { t } = useTranslation();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [tokens, setTokens] = useState<ApprovalToken[]>([]);
  const [linkedContractId, setLinkedContractId] = useState<string | null>(null);
  const [approvalLinksOpen, setApprovalLinksOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");
  const isAdmin = hasRole("admin");

  // Revoke object URL when preview closes / changes
  // No blob cleanup needed — we use data URLs which are GC'd with state.

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editScopeBlocks, setEditScopeBlocks] = useState<EditScopeBlock[]>([
    { id: crypto.randomUUID(), type: "scope", content: "" },
  ]);
  const [editTaxPercent, setEditTaxPercent] = useState<number>(0);
  const [editExpiry, setEditExpiry] = useState<string>("");
  const [editItems, setEditItems] = useState<
    Array<{ id?: string; description: string; quantity: number; unit_price: number }>
  >([]);
  const [editScopePhotos, setEditScopePhotos] = useState<ScopePhoto[]>([]);

  const startEditing = () => {
    if (!estimate) return;
    setEditTitle(estimate.title);
    setEditScopeBlocks(parseEditScopeBlocks(estimate.description));
    setEditTaxPercent(Number(estimate.tax_percent) || 0);
    setEditExpiry(estimate.expiry_date ?? "");
    setEditItems(
      items.map((it) => ({
        id: it.id,
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unit_price: Number(it.unit_price) || 0,
      })),
    );
    setEditing(true);
    void hydrateScopePhotos("estimate-scope-photos", estimate.scope_photos).then(setEditScopePhotos);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const updateEditItem = (
    idx: number,
    patch: Partial<{ description: string; quantity: number; unit_price: number }>,
  ) => {
    setEditItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addEditItem = () =>
    setEditItems((p) => [...p, { description: "", quantity: 1, unit_price: 0 }]);
  const removeEditItem = (idx: number) =>
    setEditItems((p) => (p.length === 1 ? p : p.filter((_, i) => i !== idx)));

  const editSubtotal = editItems.reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  const editTaxAmount = (editSubtotal * (editTaxPercent || 0)) / 100;
  const editTotal = editSubtotal + editTaxAmount;

  const performSaveEdit = async (resetToDraft: boolean) => {
    if (!estimate) return;
    if (!editTitle.trim()) {
      toast.error(t("pages.estimates.detail.titleRequired"));
      return;
    }
    const cleanItems = editItems.filter((i) => i.description.trim());
    if (cleanItems.length === 0) {
      toast.error(t("pages.estimates.detail.addLineItem"));
      return;
    }
    setSavingEdit(true);
    try {
      // 1. Update estimate fields (and reset to draft if needed)
      const updates: {
        title: string;
        description: string | null;
        tax_percent: number;
        expiry_date: string | null;
        scope_photos: ReturnType<typeof serializeScopePhotos>;
        status?: Status;
      } = {
        title: editTitle.trim(),
        description: (() => {
          // Serialize scope blocks back into the markdown-style description
          // ("## Heading\nbody"). Headings are localised to the current UI
          // language so the saved estimate matches what the editor showed.
          const sections: string[] = [];
          for (const block of editScopeBlocks) {
            const content = block.content.trim();
            if (!content) continue;
            const heading = t(SCOPE_LABEL_KEYS[block.type]);
            sections.push(`## ${heading}\n${content}`);
          }
          const full = sections.join("\n\n");
          return full || null;
        })(),
        tax_percent: editTaxPercent,
        expiry_date: editExpiry || null,
        scope_photos: serializeScopePhotos(editScopePhotos),
      };
      if (resetToDraft) updates.status = "draft";

      const { error: uErr } = await supabase
        .from("estimates")
        .update(updates)
        .eq("id", estimate.id);
      if (uErr) throw uErr;

      // 2. Replace line items (delete all, insert fresh)
      const { error: dErr } = await supabase
        .from("estimate_items")
        .delete()
        .eq("estimate_id", estimate.id);
      if (dErr) throw dErr;

      const { error: insErr } = await supabase.from("estimate_items").insert(
        cleanItems.map((it, idx) => ({
          estimate_id: estimate.id,
          description: it.description.trim(),
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          sort_order: idx,
        })),
      );
      if (insErr) throw insErr;

      // 3. If resetting to draft, revoke all pending approval tokens
      if (resetToDraft) {
        const { error: rErr } = await supabase
          .from("estimate_approval_tokens")
          .update({ status: "revoked" })
          .eq("estimate_id", estimate.id)
          .eq("status", "sent");
        if (rErr) {
          // Non-fatal: log but don't fail the whole save
          console.error("Could not revoke pending tokens", rErr);
        }
      }

      toast.success(
        resetToDraft
          ? t("pages.estimates.detail.saveSuccessReverted")
          : t("pages.estimates.detail.saveSuccess"),
      );
      setEditing(false);
      setConfirmReset(false);
      await load();
    } catch (e) {
      // Log the full error so we can diagnose failures that don't surface a
      // useful message (e.g. PostgrestError, network blips, RLS rejections).
      console.error("[performSaveEdit] failed", e);
      const msg =
        (e as { message?: string; details?: string; hint?: string } | null)?.message ||
        (e as { details?: string } | null)?.details ||
        (typeof e === "string" ? e : null) ||
        t("pages.estimates.detail.saveFailed");
      toast.error(t("pages.estimates.detail.saveFailed"), { description: msg });
    } finally {
      setSavingEdit(false);
    }
  };

  const saveEdit = () => {
    if (!estimate) return;
    // If estimate has been sent/approved/rejected, ask for confirmation before reverting to draft
    if (estimate.status !== "draft") {
      setConfirmReset(true);
    } else {
      performSaveEdit(false);
    }
  };

  const load = async () => {
    setLoading(true);
    const [
      { data: est, error: eErr },
      { data: its, error: iErr },
      { data: tks },
      { data: contract },
    ] = await Promise.all([
      supabase
        .from("estimates")
        .select("*, clients(name, email)")
        .eq("id", estimateId)
        .maybeSingle(),
      supabase.from("estimate_items").select("*").eq("estimate_id", estimateId).order("sort_order"),
      supabase
        .from("estimate_approval_tokens")
        .select(
          "id, token, status, client_email, created_at, responded_at, response_notes, client_signature_data, client_signature_name",
        )
        .eq("estimate_id", estimateId)
        .order("created_at", { ascending: false }),
      supabase.from("contracts").select("id").eq("estimate_id", estimateId).limit(1).maybeSingle(),
    ]);
    if (eErr) toast.error(t("pages.estimates.detail.loadFailed"), { description: eErr.message });
    if (iErr)
      toast.error(t("pages.estimates.detail.loadItemsFailed"), { description: iErr.message });
    setEstimate((est ?? null) as Estimate | null);
    setItems((its ?? []) as Item[]);
    setTokens((tks ?? []) as ApprovalToken[]);
    setLinkedContractId(contract?.id ?? null);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  const updateStatus = async (status: Status) => {
    if (!estimate) return;
    const { error } = await supabase.from("estimates").update({ status }).eq("id", estimate.id);
    if (error) {
      toast.error(t("pages.estimates.detail.statusFailed"), { description: error.message });
    } else {
      setEstimate({ ...estimate, status });
      toast.success(t("pages.estimates.detail.statusUpdated", { status: t(`statuses.${status}`) }));
    }
  };

  const onDelete = async () => {
    if (!estimate) return;
    const { error } = await supabase.from("estimates").delete().eq("id", estimate.id);
    if (error) {
      toast.error(t("pages.estimates.detail.deleteFailed"), { description: error.message });
    } else {
      toast.success(t("pages.estimates.detail.deleted"));
      navigate({ to: "/estimates" });
    }
  };

  const openSendDialog = () => {
    setSendEmail(estimate?.clients?.email ?? "");
    setSendMessage("");
    setSendOpen(true);
  };

  const sendApprovalLink = async () => {
    if (!estimate) return;
    const email = sendEmail.trim();
    if (!email) {
      toast.error(t("pages.estimates.detail.recipientRequired"));
      return;
    }
    setSending(true);
    try {
      // 1) Generate the PDF and upload to public storage so we can attach the
      //    download link to the email the client receives.
      let pdfPublicUrl: string | undefined;
      try {
        const { pdfBase64, filename } = await generateEstimatePdf({
          data: { estimateId: estimate.id },
        });
        const bin = atob(pdfBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const safeName = (filename || `estimate-${estimate.number}.pdf`).replace(
          /[^a-zA-Z0-9._-]+/g,
          "-",
        );
        const storagePath = `${estimate.id}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from("estimate-pdfs")
          .upload(storagePath, new Blob([bytes], { type: "application/pdf" }), {
            contentType: "application/pdf",
            upsert: true,
          });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("estimate-pdfs").getPublicUrl(storagePath);
        pdfPublicUrl = pub?.publicUrl;
      } catch (pdfErr) {
        // Don't block sending the link if PDF generation/upload fails.
        console.error("PDF generation/upload failed", pdfErr);
      }

      const { data, error } = await supabase.functions.invoke("send-estimate-approval", {
        body: {
          estimate_id: estimate.id,
          recipient_email: email,
          message: sendMessage.trim() || undefined,
          app_origin: typeof window !== "undefined" ? window.location.origin : undefined,
          pdf_url: pdfPublicUrl,
        },
      });
      if (error) throw error;
      toast.success(t("pages.estimates.detail.linkSent"), {
        description: data?.approval_url
          ? t("pages.estimates.detail.linkSentDescQueuedCopied")
          : t("pages.estimates.detail.linkSentDescQueued"),
      });
      if (data?.approval_url && typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(data.approval_url).catch(() => {});
      }
      setSendOpen(false);
      load();
    } catch (e: any) {
      toast.error(t("pages.estimates.detail.sendFailed"), {
        description: e?.message ?? t("common.error"),
      });
    } finally {
      setSending(false);
    }
  };

  const openPdfPreview = async () => {
    if (!estimate) return;
    setDownloadingPdf(true);
    try {
      const { pdfBase64, filename } = await generateEstimatePdf({
        data: { estimateId: estimate.id },
      });
      // Convert base64 -> Blob -> object URL for the iframe
      const bin = atob(pdfBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      // Use a data URL instead of a blob URL — sandboxed/preview iframes
      // (e.g. Chrome inside Lovable preview) block blob: URLs in <iframe>.
      const url = `data:application/pdf;base64,${pdfBase64}`;
      setPdfPreviewUrl(url);
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename || `orcamento-${estimate.number}.pdf`);
      setPdfPreviewOpen(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("pages.estimates.detail.pdfGenerateFailed");
      toast.error(t("pages.estimates.detail.pdfGenerateFailed"), { description: msg });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(
      pdfPreviewFilename || `orcamento-${estimate?.number ?? "estimate"}.pdf`,
      pdfPreviewBase64,
    );
    toast.success(t("pages.estimates.detail.pdfDownloaded"));
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPdfPreviewUrl(null);
    setPdfPreviewBase64(null);
  };

  const copyApprovalLink = async (token: string) => {
    const url = `${window.location.origin}/estimates/approve/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("pages.estimates.detail.copyLinkSuccess"));
    } catch {
      toast.error(t("pages.estimates.detail.copyLinkFailed"), { description: url });
    }
  };

  const revokeToken = async (id: string) => {
    const { error } = await supabase
      .from("estimate_approval_tokens")
      .update({ status: "revoked" })
      .eq("id", id);
    if (error) {
      toast.error(t("pages.estimates.detail.revokeFailed"), { description: error.message });
    } else {
      toast.success(t("pages.estimates.detail.linkRevoked"));
      load();
    }
  };

  const formatMoney = (v: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: estimate?.currency || "USD",
    }).format(Number(v) || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="rounded-lg border border-border bg-card p-12 text-center">
        <p className="font-medium">{t("pages.estimates.detail.notFound")}</p>
        <Link to="/estimates" className="mt-2 inline-block text-sm text-primary hover:underline">
          {t("pages.estimates.detail.back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/estimates" })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{estimate.number}</span>
              <Badge variant={statusVariant[estimate.status]} className="capitalize">
                {t(`statuses.${estimate.status}`)}
              </Badge>
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">{estimate.title}</h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button
                variant="outline"
                className="gap-2"
                onClick={cancelEditing}
                disabled={savingEdit}
              >
                <X className="h-4 w-4" />
                {t("common.cancel")}
              </Button>
              <Button className="gap-2" onClick={saveEdit} disabled={savingEdit}>
                {savingEdit ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {savingEdit
                  ? t("pages.estimates.detail.savingDots")
                  : t("pages.estimates.detail.saveChanges")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="gap-2" onClick={startEditing}>
                <Pencil className="h-4 w-4" />
                {t("common.edit")}
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={openPdfPreview}
                disabled={downloadingPdf}
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                {downloadingPdf
                  ? t("pages.estimates.detail.generatingPdf")
                  : t("pages.estimates.detail.viewPdf")}
              </Button>
              <Button variant="outline" className="gap-2" onClick={openSendDialog}>
                <Send className="h-4 w-4" />
                {t("pages.estimates.detail.sendToClient")}
              </Button>
              {estimate.status === "approved" &&
                (linkedContractId ? (
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() =>
                      navigate({
                        to: "/contracts/$contractId",
                        params: { contractId: linkedContractId },
                      })
                    }
                  >
                    <FileSignature className="h-4 w-4" />
                    {t("pages.estimates.detail.viewContract")}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() =>
                      navigate({
                        to: "/contracts/new",
                        search: { estimateId: estimate.id },
                      })
                    }
                  >
                    <FileSignature className="h-4 w-4" />
                    {t("pages.estimates.detail.convert")}
                  </Button>
                ))}
              <Select value={estimate.status} onValueChange={(v) => updateStatus(v as Status)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t("statuses.draft")}</SelectItem>
                  <SelectItem value="sent">{t("statuses.sent")}</SelectItem>
                  <SelectItem value="approved">{t("statuses.approved")}</SelectItem>
                  <SelectItem value="rejected">{t("statuses.rejected")}</SelectItem>
                  <SelectItem value="expired">{t("statuses.expired")}</SelectItem>
                </SelectContent>
              </Select>
              {isAdmin && (
                <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {editing && estimate.status !== "draft" && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <p className="font-medium">
            {t("pages.estimates.detail.editingWarn", { status: t(`statuses.${estimate.status}`) })}
          </p>
          <p
            className="text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: t("pages.estimates.detail.editingWarnDesc") }}
          />
        </div>
      )}

      {tokens.length > 0 && (
        <div className="rounded-lg border border-border bg-card">
          <button
            type="button"
            onClick={() => setApprovalLinksOpen((o) => !o)}
            className="flex w-full items-center justify-between border-b border-border p-4 text-left hover:bg-muted/40"
            aria-expanded={approvalLinksOpen}
          >
            <p className="text-sm font-semibold">{t("pages.estimates.detail.approvalLinks")}</p>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${approvalLinksOpen ? "rotate-180" : ""}`}
            />
          </button>
          {approvalLinksOpen && (
            <div className="divide-y divide-border">
              {tokens.map((tk) => (
                <div
                  key={tk.id}
                  className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-start gap-3">
                    {tk.status === "approved" ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                    ) : tk.status === "rejected" ? (
                      <XCircle className="mt-0.5 h-4 w-4 text-destructive" />
                    ) : (
                      <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {tk.status === "sent"
                          ? t("pages.estimates.detail.awaitingResponse")
                          : t(`statuses.${tk.status}`)}
                        {tk.client_email && (
                          <span className="ml-2 font-normal text-muted-foreground">
                            · {tk.client_email}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("pages.estimates.detail.sentOn", {
                          date: new Date(tk.created_at).toLocaleString(),
                        })}
                        {tk.responded_at &&
                          t("pages.estimates.detail.respondedOn", {
                            date: new Date(tk.responded_at).toLocaleString(),
                          })}
                      </p>
                      {tk.response_notes && (
                        <p className="mt-1 text-sm whitespace-pre-wrap text-muted-foreground">
                          “{tk.response_notes}”
                        </p>
                      )}
                      {tk.status === "approved" && !tk.client_signature_data && (
                        <p className="mt-1 text-xs font-medium text-destructive">
                          {t("pages.estimates.detail.approvedInvalidNoSignature")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {tk.status === "approved" && tk.client_signature_data && (
                      <Button
                        variant="default"
                        size="sm"
                        className="gap-2"
                        onClick={() =>
                          window.open(
                            `${window.location.origin}/estimates/approve/${tk.token}`,
                            "_blank",
                          )
                        }
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {t("pages.estimates.detail.viewSigned")}
                      </Button>
                    )}
                    {tk.status === "sent" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => copyApprovalLink(tk.token)}
                        >
                          <Copy className="h-3.5 w-3.5" /> {t("pages.estimates.detail.copyLink")}
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => revokeToken(tk.id)}>
                            {t("pages.estimates.detail.revoke")}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <EmailSendHistoryCard kind="estimate" estimateId={estimate.id} />

      {estimate.status === "approved" && (
        <ChangeOrdersCard
          estimateId={estimate.id}
          estimateCurrency={estimate.currency}
          estimateTotal={Number(estimate.total) || 0}
        />
      )}

      {editing ? (
        <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="edit-title">{t("pages.estimates.detail.editTitleLabel")}</Label>
            <Input
              id="edit-title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <Label>{t("pages.estimates.form.scopeSection")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("pages.estimates.form.scopeHint")}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() =>
                  setEditScopeBlocks((p) => [
                    ...p,
                    { id: crypto.randomUUID(), type: "scope", content: "" },
                  ])
                }
              >
                <Plus className="h-3.5 w-3.5" />
                {t("pages.estimates.form.addBlock")}
              </Button>
            </div>
            <div className="space-y-2">
              {editScopeBlocks.map((block, idx) => {
                const opt = SCOPE_OPTIONS.find((o) => o.value === block.type)!;
                const Icon = opt.icon;
                return (
                  <div key={block.id} className="rounded-lg border border-border bg-background p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <Select
                        value={block.type}
                        onValueChange={(value) =>
                          setEditScopeBlocks((prev) =>
                            prev.map((b, i) =>
                              i === idx ? { ...b, type: value as ScopeType } : b,
                            ),
                          )
                        }
                      >
                        <SelectTrigger className="h-8 w-auto gap-2 px-2">
                          <Icon className={`h-3.5 w-3.5 ${opt.color}`} />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SCOPE_OPTIONS.map((option) => {
                            const OptIcon = option.icon;
                            return (
                              <SelectItem key={option.value} value={option.value}>
                                <span className="flex items-center gap-2">
                                  <OptIcon className={`h-3.5 w-3.5 ${option.color}`} />
                                  {t(SCOPE_LABEL_KEYS[option.value])}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {editScopeBlocks.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="ml-auto h-8 px-2 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setEditScopeBlocks((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    <Textarea
                      rows={4}
                      placeholder={t(SCOPE_PLACEHOLDER_KEYS[block.type])}
                      value={block.content}
                      onChange={(e) =>
                        setEditScopeBlocks((prev) =>
                          prev.map((b, i) => (i === idx ? { ...b, content: e.target.value } : b)),
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-expiry">{t("pages.estimates.detail.expiryDate")}</Label>
            <Input
              id="edit-expiry"
              type="date"
              value={editExpiry}
              onChange={(e) => setEditExpiry(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-tax">{t("pages.estimates.detail.taxPercentLabel")}</Label>
            <Input
              id="edit-tax"
              type="number"
              step="0.01"
              min="0"
              value={editTaxPercent}
              onChange={(e) => setEditTaxPercent(Number(e.target.value) || 0)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("pages.estimates.form.scopePhotos")}</Label>
            <ScopePhotosUploader
              bucket="estimate-scope-photos"
              companyId={estimate.company_id}
              photos={editScopePhotos}
              setPhotos={setEditScopePhotos}
            />
          </div>
        </div>
      ) : (
        estimate.description && (
          <div className="space-y-3">
            {parseScopeBlocks(estimate.description).map((block, idx) => (
              <div key={idx} className="rounded-lg border border-border bg-card p-5">
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {block.title}
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{block.body}</p>
              </div>
            ))}
          </div>
        )
      )}

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-4">
          <p className="text-sm font-semibold">{t("pages.estimates.detail.lineItems")}</p>
          {editing && (
            <Button variant="outline" size="sm" onClick={addEditItem} className="gap-2">
              <Plus className="h-3.5 w-3.5" />
              {t("pages.estimates.detail.addItem")}
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.estimates.detail.description")}</TableHead>
                <TableHead className={editing ? "w-24" : "w-20 text-right"}>
                  {t("pages.estimates.detail.qty")}
                </TableHead>
                <TableHead className={editing ? "w-32" : "w-32 text-right"}>
                  {t("pages.estimates.detail.unitPrice")}
                </TableHead>
                <TableHead className="w-32 text-right">
                  {t("pages.estimates.detail.total")}
                </TableHead>
                {editing && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {editing
                ? editItems.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Input
                          value={it.description}
                          onChange={(e) => updateEditItem(idx, { description: e.target.value })}
                          placeholder={t("pages.estimates.detail.itemPlaceholder")}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.quantity}
                          onChange={(e) =>
                            updateEditItem(idx, { quantity: Number(e.target.value) })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={it.unit_price}
                          onChange={(e) =>
                            updateEditItem(idx, { unit_price: Number(e.target.value) })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatMoney((Number(it.quantity) || 0) * (Number(it.unit_price) || 0))}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeEditItem(idx)}
                          disabled={editItems.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                : items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>{it.description}</TableCell>
                      <TableCell className="text-right">{it.quantity}</TableCell>
                      <TableCell className="text-right">{formatMoney(it.unit_price)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatMoney(it.total)}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
          <div className="flex w-56 justify-between">
            <span className="text-muted-foreground">{t("pages.estimates.detail.subtotal")}</span>
            <span>{formatMoney(editing ? editSubtotal : estimate.subtotal)}</span>
          </div>
          <div className="flex w-56 justify-between">
            <span className="text-muted-foreground">
              {t("pages.estimates.detail.taxLabel", {
                percent: editing ? editTaxPercent : estimate.tax_percent,
              })}
            </span>
            <span>{formatMoney(editing ? editTaxAmount : estimate.tax_amount)}</span>
          </div>
          <div className="mt-1 flex w-56 justify-between border-t border-border pt-1 text-base font-semibold">
            <span>{t("pages.estimates.detail.total")}</span>
            <span>{formatMoney(editing ? editTotal : estimate.total)}</span>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.estimates.detail.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription
              dangerouslySetInnerHTML={{
                __html: t("pages.estimates.detail.deleteDesc", { number: estimate.number }),
              }}
            />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.estimates.detail.confirmResetTitle")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1 text-sm">
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("pages.estimates.detail.confirmResetLine1", {
                      status: t(`statuses.${estimate.status}`),
                    }),
                  }}
                />
                <p
                  dangerouslySetInnerHTML={{
                    __html: `• ${t("pages.estimates.detail.confirmResetLine2")}`,
                  }}
                />
                <p>• {t("pages.estimates.detail.confirmResetLine3")}</p>
                <p>• {t("pages.estimates.detail.confirmResetLine4")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingEdit}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => performSaveEdit(true)} disabled={savingEdit}>
              {savingEdit
                ? t("pages.estimates.detail.savingDots")
                : t("pages.estimates.detail.saveAndRevert")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.estimates.detail.sendDialogTitle")}</DialogTitle>
            <DialogDescription>{t("pages.estimates.detail.sendDialogDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="send-email">{t("pages.estimates.detail.recipientEmail")}</Label>
              <Input
                id="send-email"
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder={t("pages.estimates.detail.recipientPlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="send-message">{t("pages.estimates.detail.messageOptional")}</Label>
              <Textarea
                id="send-message"
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
                rows={3}
                placeholder={t("pages.estimates.detail.sendDialogPlaceholder")}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>
              {t("common.cancel")}
            </Button>
            <Button onClick={sendApprovalLink} disabled={sending} className="gap-2">
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sending ? t("pages.estimates.detail.sending") : t("pages.estimates.detail.sendLink")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pdfPreviewOpen}
        onOpenChange={(open) => (open ? setPdfPreviewOpen(true) : closePdfPreview())}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t("pages.estimates.detail.pdfPreviewDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("pages.estimates.detail.pdfPreviewDialogDesc", { filename: pdfPreviewFilename })}
            </DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] w-full overflow-hidden rounded-md border border-border bg-muted">
            {pdfPreviewBase64 ? (
              <PdfCanvasPreview pdfBase64={pdfPreviewBase64} />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePdfPreview}>
              {t("pages.estimates.detail.pdfClose")}
            </Button>
            <Button onClick={confirmDownloadPdf} disabled={!pdfPreviewBase64} className="gap-2">
              <FileDown className="h-4 w-4" />
              {t("pages.estimates.detail.pdfDownload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <p className="mt-6 text-center text-[11px] italic text-muted-foreground">
        {t("pages.estimates.detail.footerNote")}
      </p>
    </div>
  );
}
